import "./Legal.scss";

export default function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: March 10, 2026</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to SOMIGO ("we," "our," or "us"). We respect your privacy
            and are committed to protecting your personal data. This privacy
            policy explains how we collect, use, and safeguard your information
            when you visit our website and use our services.
          </p>
          <p>
            This Privacy Policy should be read in conjunction with our{" "}
            <a href="/legal/terms-of-service">Terms of Service</a>, which govern
            your use of our platform.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li>Account information (name, email address, password)</li>
            <li>Profile information and preferences</li>
            <li>Content you create (tasks, notes, projects)</li>
            <li>Communications with us</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li>Log data (IP address, browser type, pages visited)</li>
            <li>Device information</li>
            <li>Cookies and similar tracking technologies</li>
            <li>Usage data and analytics</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>Process your transactions and send notifications</li>
            <li>Respond to your requests and provide customer support</li>
            <li>
              Send you updates, marketing materials, and other information
            </li>
            <li>Protect against fraudulent or illegal activity</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Legal Basis for Processing</h2>
          <p>
            Under GDPR and applicable data protection laws, we process your
            personal data based on the following legal grounds:
          </p>
          <ul>
            <li>
              <strong>Contractual Necessity:</strong> We process your data to
              provide the SOMIGO service you requested and to fulfill our
              contractual obligations to you.
            </li>
            <li>
              <strong>Legitimate Interest:</strong> We process certain technical
              and usage data to maintain, secure, and improve our service. This
              is necessary for our legitimate business interests.
            </li>
            <li>
              <strong>Consent:</strong> We process analytics cookies and
              marketing communications only with your explicit consent, which
              you can withdraw at any time.
            </li>
            <li>
              <strong>Legal Obligations:</strong> We may retain certain billing,
              accounting, or other information as required by applicable law.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Cookies and Tracking Technologies</h2>
          <p>We use the following types of cookies:</p>

          <div className="cookie-table">
            <div className="cookie-row">
              <div className="cookie-type">
                <strong>Strictly Necessary Cookies</strong>
              </div>
              <div className="cookie-desc">
                Essential for the website to function. Cannot be disabled.
              </div>
            </div>
            <div className="cookie-row">
              <div className="cookie-type">
                <strong>Analytics Cookies</strong>
              </div>
              <div className="cookie-desc">
                Help us understand how visitors use our website (Google
                Analytics).
              </div>
            </div>
            <div className="cookie-row">
              <div className="cookie-type">
                <strong>Marketing Cookies</strong>
              </div>
              <div className="cookie-desc">
                Track visitors across websites to display relevant ads.
              </div>
            </div>
          </div>

          <p>
            You can manage your cookie preferences at any time using our cookie
            consent banner or your browser settings.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Data Sharing and Disclosure</h2>
          <p>We may share your information with:</p>
          <ul>
            <li>
              <strong>Service Providers:</strong> Third-party companies that
              help us operate our business (hosting, analytics, payment
              processing)
            </li>
            <li>
              <strong>Business Transfers:</strong> In the event of a merger,
              acquisition, or sale of assets
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to
              protect our rights
            </li>
          </ul>
          <p>
            We do not sell, rent, or trade your personal information to third
            parties.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to
            protect your personal data against unauthorized access, alteration,
            disclosure, or destruction. Our security measures include:
          </p>
          <ul>
            <li>Data encryption in transit and at rest</li>
            <li>Secure hosting in European Union data centers</li>
            <li>Regular security audits and monitoring</li>
            <li>Access controls and authentication</li>
            <li>Regular backups and disaster recovery procedures</li>
          </ul>
          <p>
            However, no method of transmission over the internet is 100% secure.
            While we strive to protect your data, we cannot guarantee absolute
            security.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Your Rights</h2>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li>Access, correct, or delete your personal data</li>
            <li>Object to or restrict processing of your data</li>
            <li>Data portability</li>
            <li>Withdraw consent</li>
            <li>
              Lodge a complaint with the Danish Data Protection Authority
              (Datatilsynet)
            </li>
          </ul>
          <p>
            To exercise these rights, please contact us at{" "}
            <a href="mailto:contact@somigo.io">contact@somigo.io</a>
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Data Retention</h2>
          <p>
            We retain your personal data for as long as necessary to provide our
            services and comply with legal obligations. Specific retention
            periods by data category:
          </p>
          <ul>
            <li>
              <strong>Account Data:</strong> Retained while your account is
              active and for 30 days after account deletion
            </li>
            <li>
              <strong>Usage Logs and Analytics:</strong> Retained for up to 12
              months
            </li>
            <li>
              <strong>Billing Records:</strong> Retained for 5 years as required
              by accounting and tax regulations
            </li>
            <li>
              <strong>Support Communications:</strong> Retained for up to 2
              years for quality assurance and issue resolution
            </li>
            <li>
              <strong>Backups:</strong> Automated backups may persist for up to
              90 days but are securely isolated and automatically overwritten
            </li>
          </ul>
          <p>
            When you delete your account, we will immediately delete or
            anonymize your active data. Backup copies may persist for up to 90
            days in our secure backup systems before being permanently
            overwritten.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. International Data Transfers</h2>
          <p>
            <strong>EU-Based Hosting:</strong> SOMIGO and all user data are
            hosted within the European Union. Our servers and data storage
            facilities are located in EU data centers, ensuring compliance with
            the General Data Protection Regulation (GDPR) and European data
            protection standards.
          </p>
          <p>
            Some third-party services we use (such as payment processing via
            Stripe) may process data outside the EU. In such cases, we ensure
            appropriate safeguards are in place, including Standard Contractual
            Clauses (SCCs) approved by the European Commission, to protect your
            data in accordance with GDPR requirements.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Children's Privacy</h2>
          <p>
            Our services are not intended for children under 16 years of age. We
            do not knowingly collect personal data from children. If you believe
            we have collected information from a child, please contact us
            immediately.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. We will notify
            you of any material changes by posting the new policy on this page
            and updating the "Last Updated" date.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our data
            practices, please contact us:
          </p>
          <ul className="contact-list">
            <li>
              Email: <a href="mailto:contact@somigo.io">contact@somigo.io</a>
            </li>
            <li>Company: Happyhill</li>
            <li>CVR: 34028370</li>
            <li>Address: Topkærvej 7, 8200 Aarhus N, Denmark</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
